<template>
  <div>
    <v-card flat>
      <v-card-title class="secondary white--text">
        <div class="ma-0">
          <div class="d-flex align-center">
            <v-icon large left color="white">mdi-tools</v-icon>
            <p class="subtitle-1 my-0">{{ tableData.Count }} Contractors</p>
            <v-divider vertical class="mx-3 my-1" color="white"></v-divider>
            <span class="subtitle-1">{{ totalPayments }}</span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <generic-search
          v-model="contractorId"
          label="Search Contractor"
          schema-type="MaintenanceContractor"
          :hide-details="true"
          :dark="true"
          @change="handleFilterChange"
        />
      </v-card-title>

      <!-- Table -->
      <v-data-table
        :headers="tableHeaders"
        :items="tableData && tableData.Data ? tableData.Data : []"
        :loading="isLoading"
        :search="search"
        :server-items-length="tableData && tableData.Count ? tableData.Count : 0"
        :options.sync="options"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        @update:options="handleOptionsUpdate"
        class="elevation-0"
        no-data-text="No contractors found"
        :items-per-page="10"
      >
        <template v-slot:item.ContractorName="{ item }">
          <a class="accent--text" @click="openInfoDialog(item)">
            {{ item.ContractorName }}
          </a>
        </template>
        
        <template v-slot:item.DisplayContractorBalance="{ item }">
          <span>{{ item.DisplayContractorBalance }}</span>
        </template>
        
        <template v-slot:item.AccountNumber="{ item }">
          <span
            class="font-weight-thin"
            v-if="item.AccountNumber === 'Bank Details Incomplete'"
          >
            Incomplete
          </span>
          <span class="font-weight-thin" v-else>
            {{ item.AccountNumber }}
          </span>
        </template>
        
        <template v-slot:item.actions="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="secondary"
                dark
                small
                depressed
                v-bind="attrs"
                v-on="on"
                class="text-capitalize"
              >
                Actions
                <v-icon right>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list class="py-0 secondary" dark>
              <v-list-item @click="fetchAccountSummary(item.OID)">
                <v-list-item-title>View account summary</v-list-item-title>
                <v-list-item-icon>
                  <v-icon small>mdi-open-in-new</v-icon>
                </v-list-item-icon>
              </v-list-item>
              <v-list-item v-if="item.Email" @click="openActionDialog(item)">
                <v-list-item-title>Invite contractor to use Contractor app</v-list-item-title>
                <v-list-item-icon>
                  <v-icon small>mdi-email</v-icon>
                </v-list-item-icon>
              </v-list-item>
              <v-list-item @click="applyCharge('Charge', item)">
                <v-list-item-title>Apply Charge</v-list-item-title>
                <v-list-item-icon>
                  <v-icon small>mdi-currency-gbp</v-icon>
                </v-list-item-icon>
              </v-list-item>
              <v-list-item @click="applyCharge('Refund', item)">
                <v-list-item-title>Apply Refund</v-list-item-title>
                <v-list-item-icon>
                  <v-icon small>mdi-currency-gbp</v-icon>
                </v-list-item-icon>
              </v-list-item>
              <v-list-item @click="navigateToPaymentRequests(item)">
                <v-list-item-title>View payment requests</v-list-item-title>
                <v-list-item-icon>
                  <v-icon small>mdi-folder-plus-outline</v-icon>
                </v-list-item-icon>
              </v-list-item>
              <v-list-item @click="openDialog(item)">
                <v-list-item-title>Contact support regarding this contractor</v-list-item-title>
                <v-list-item-icon>
                  <v-icon small>mdi-comment-text-outline</v-icon>
                </v-list-item-icon>
              </v-list-item>
              <v-list-item @click="requestOneOffPayment(item)">
                <v-list-item-title>Request One Off Payment</v-list-item-title>
                <v-list-item-icon>
                  <v-icon small>mdi-currency-gbp</v-icon>
                </v-list-item-icon>
              </v-list-item>
              <v-list-item @click="fetchPendingInvoice(item.OID)">
                <v-list-item-title>View latest pending invoice</v-list-item-title>
                <v-list-item-icon>
                  <v-icon small>mdi-open-in-new</v-icon>
                </v-list-item-icon>
              </v-list-item>
              <v-list-item @click.stop="openHistoricInvoicesDialog(item.OID)">
                <v-list-item-title>Download latest remittances</v-list-item-title>
                <v-list-item-icon>
                  <v-icon small>mdi-download</v-icon>
                </v-list-item-icon>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialogs -->
    <v-dialog v-model="pendingInvoiceDialog" max-width="100%">
      <PendingInvoiceCard
        :invoice="pendingInvoice"
        @closeDialog="closePendingInvoiceDialog"
      />
    </v-dialog>

    <MsgDialog
      :dialog="msgDialog"
      :details="selectedItem.ContractorName"
      :source="source"
      :OID="selectedItem.OID"
      @closeDialog="closeDialog"
      @msgSent="msgSent"
    />

    <Snackbar
      :snackbarDialog="snackbar"
      :snackbarText="snackbarText"
      :snackbarColor="snackbarColor"
      @closeSnackbar="closeSnackbar"
      :timeout="timeout"
    />

    <UserInfoDialog
      :dialog="infoDialog"
      :name="selectedItem.ContractorName"
      :email="selectedItem.Email"
      :number="selectedItem.Mobile"
      :fullDetails="selectedItem"
      :selectedProperties="selectedProperties"
      :isLoading="infoLoading"
      @closeDialog="closeInfoDialog"
    />

    <v-dialog v-model="accountSummaryPopup" max-width="100%">
      <PersonAccountPopup
        :entries="accountSummary"
        @closeDialog="closeAccountSummaryPopup"
      />
    </v-dialog>

    <v-dialog v-model="applyChargePopup" max-width="500px">
      <ApplyChargeOrRefundForm
        :actionToApply="chargeAction"
        :personID="selectedItem.OID"
        @closeDialog="closeChargePopup"
      />
    </v-dialog>

    <v-dialog v-model="actionDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="dialogColor white--text" color="secondary">
          Send email
          <v-spacer></v-spacer>
          <v-btn @click="actionDialog = false" text fab small color="white">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-3">
          <v-img src="@/assets/contractorApp.png" contain width="100%"></v-img>
          <p class="body-1 mt-5">
            Would you like to send
            <span class="primary--text">{{ selectedItem.ContractorName }}</span>
            an invitation to use the contractorApp?
          </p>
          <p class="body-1">
            You can find out more about the contractorApp
            <a
              href="https://agentos.com/contractor-app/"
              class="text-decoration-none"
              target="_blank"
            >here.</a>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="sendInvitation"
            color="green d-inline"
            depressed
            width="100"
            class="mr-3 white--text"
          >
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="requestOneOffDialog" max-width="500px">
      <RequestPaymentDialog
        :selectedPaymentType="'One off payment'"
        :personID="selectedItem.OID"
        @closeRequestPaymentDialog="closeOneOffRequestPaymentDialog"
      />
    </v-dialog>

    <v-dialog
      v-model="historicInvoiceDialog"
      width="500px"
      transition="dialog-transition"
    >
      <HistoricInvoicesCard
        :invoices="selectedHistoricInvoices"
        @closeDialog="closeHistoricInvoiceDialog"
      />
    </v-dialog>
  </div>
</template>

<script>
import { amplifyProxy } from '@/services/directApi';
import MsgDialog from "@/components/Dialogs/MessageDialog";
import UserInfoDialog from "@/components/Dialogs/UserInfoDialog";
import Snackbar from "@/components/Snackbars/TheSnackbar";
import PersonAccountPopup from "@/components/BaseComponents/PersonAccountPopup.vue";
import ApplyChargeOrRefundForm from "../PaymentRequests/ApplyChargeOrRefundForm.vue";
import RequestPaymentDialog from '../PaymentRequests/PaymentRequestDialog.vue';
import HistoricInvoicesCard from "@/components/OutgoingPayments/HistoricInvoicesCard.vue";
import PendingInvoiceCard from "@/components/OutgoingPayments/PendingInvoiceCard.vue";
import GenericSearch from '@/components/CloudSearch/GenericSearch.vue';

export default {
  name: 'ContractorsRecordsTable',
  components: {
    MsgDialog,
    UserInfoDialog,
    Snackbar,
    PersonAccountPopup,
    ApplyChargeOrRefundForm,
    RequestPaymentDialog,
    HistoricInvoicesCard,
    PendingInvoiceCard,
    GenericSearch
  },
  props: {
    tableData: {
      type: Object,
      required: true
    },
    headers: {
      type: Array,
      required: true
    },
    msg: {
      type: String,
      default: ''
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      source: "Contractors",
      search: "",
      selectedItem: "",
      msgDialog: false,
      infoDialog: false,
      actionDialog: false,
      invitationSubject: "AgentOS Contractor app",
      snackbar: false,
      snackbarText: "",
      snackbarColor: "",
      timeout: 3000,
      accountSummary: "",
      accountSummaryPopup: false,
      chargeAction: "",
      applyChargePopup: false,
      requestOneOffDialog: false,
      selectedProperties: [],
      infoLoading: false,
      historicInvoiceDialog: false,
      selectedHistoricInvoices: "",
      pendingInvoiceDialog: false,
      pendingInvoice: "",
      loadingPendingInvoice: false,
      options: {
        page: 1,
        itemsPerPage: 10
      },
      tableHeaders: [
        { text: 'Name', value: 'ContractorName', sortable: false },
        { text: 'Balance', value: 'DisplayContractorBalance', sortable: false },
        { text: 'Account no.', value: 'AccountNumber', sortable: false },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false }
      ],
      // Filter data
      contractorId: ''
    };
  },
  computed: {
    hasActiveFilters() {
      return this.contractorId
    },
    totalPayments() {
      if (!this.tableData || !this.tableData.Data) return '£0.00';
      const total = this.tableData.Data.reduce(
        (acc, val) => acc + (val.ContractorBalance || 0),
        0
      );
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(total);
    }
  },
  watch: {},
  methods: {
    async fetchPendingInvoice(id) {
      this.loadingPendingInvoice = true;
      try {
        const endpoint = `/${this.$store.state.user.ClientName}/outgoingPayments/${id}/invoice/pending`;
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        };

        const response = await amplifyProxy.get(endpoint, options);
        this.pendingInvoice = response.data;
        this.pendingInvoiceDialog = true;
      } catch (error) {
        console.error('Error fetching pending invoice:', error);
      } finally {
        this.loadingPendingInvoice = false;
      }
    },

    closePendingInvoiceDialog() {
      this.pendingInvoice = "";
      this.pendingInvoiceDialog = false;
    },

    async openHistoricInvoicesDialog(personID) {
      try {
        const endpoint = `/${this.$store.state.user.ClientName}/${personID}/invoices/historic`;
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        };

        const response = await amplifyProxy.get(endpoint, options);
        this.selectedHistoricInvoices = response.data;
        this.historicInvoiceDialog = true;
      } catch (error) {
        console.error('Error fetching historic invoices:', error);
      }
    },

    closeHistoricInvoiceDialog() {
      this.selectedHistoricInvoices = "";
      this.historicInvoiceDialog = false;
    },

    navigateToPaymentRequests(item) {
      this.$router.push({ name: "Payments", params: { id: item.OID } });
    },

    closeSnackbar() {
      this.snackbar = false;
    },

    openDialog(item) {
      this.selectedItem = item;
      this.msgDialog = true;
    },

    async openInfoDialog(item) {
      this.infoLoading = true;
      try {
        const endpoint = `/${this.$store.state.user.ClientName}/${item.OID}/invoices/properties`;
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        };

        const response = await amplifyProxy.get(endpoint, options);
        this.selectedProperties = response.data;
        this.selectedItem = item;
        this.infoDialog = true;
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        this.infoLoading = false;
      }
    },

    openActionDialog(item) {
      this.selectedItem = item;
      this.actionDialog = true;
    },

    closeDialog() {
      this.msgDialog = false;
      this.selectedItem = "";
    },

    closeInfoDialog() {
      this.selectedProperties = [];
      this.infoDialog = false;
      this.selectedItem = "";
    },

    sendInvitation() {
      window.location.href = `mailto:${this.selectedItem.Email}?subject=${this.invitationSubject}&body=Hi ${this.selectedItem.ContractorName},%0a%0aPlease setup your contractor account here: https://contractor.agentos.com to access work orders and payments.`;
      this.actionDialog = false;
    },

    msgSent(payload) {
      this.msgDialog = false;
      this.snackbar = true;
      this.snackbarColor = payload.color;
      this.snackbarText = payload.text;
      this.selectedItem = "";
    },

    async fetchAccountSummary(id) {
      this.loadingAccountsSummary = true;
      try {
        const endpoint = `/${this.$store.state.user.ClientName}/company/person/${id}/account`;
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        };

        const response = await amplifyProxy.get(endpoint, options);
        this.accountSummary = response.data;
        this.accountSummaryPopup = true;
      } catch (error) {
        console.error('Error fetching account summary:', error);
      } finally {
        this.loadingAccountsSummary = false;
      }
    },

    closeAccountSummaryPopup() {
      this.accountSummary = "";
      this.accountSummaryPopup = false;
    },

    applyCharge(actionToApply, item) {
      this.chargeAction = actionToApply;
      this.selectedItem = item;
      this.applyChargePopup = true;
    },

    closeChargePopup() {
      this.chargeAction = "";
      this.applyChargePopup = false;
    },

    requestOneOffPayment(item) {
      this.selectedItem = item;
      this.requestOneOffDialog = true;
    },

    closeOneOffRequestPaymentDialog() {
      this.requestOneOffDialog = false;
      this.selectedItem = "";
    },
    
    handleOptionsUpdate(newOptions) {
      if (this.search || this.isLoading) return // Don't emit for client-side search or while loading
      
      let type = 'all';
      if (this.msg.includes('bank')) type = 'bankless';
      else if (this.msg.includes('email')) type = 'emailless';
      else if (this.msg.includes('Insufficient')) type = 'onHold';
      
      this.$emit('paginate', type, newOptions.page, newOptions.itemsPerPage, { contractorId: this.contractorId });
    },
    handleFilterChange() {
      // Reset to first page when filters change
      this.options.page = 1;
      this.handleOptionsUpdate(this.options);
    },
    clearAllFilters() {
      this.contractorId = '';
      this.handleFilterChange();
    }
  }
};
</script>

<style lang="scss" scoped>
.tableColor {
  background-color: white;
}

.dialogColor {
  background-color: var(--v-secondary-base) !important;
}
</style>